import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import {CorporateScenarioContent} from './CorporateScenarioContent';
import {ExercisePhasesCard} from './ExercisePhasesCard';
import {ExerciseTeamMappingsCard} from './ExerciseTeamMappingsCard';
import {WalkthroughsCard} from './WalkthroughsCard';
import {ScenarioInjectsTab} from './ScenarioInjectsTab';
import {ExerciseFlagsCard} from './ExerciseFlagsCard';
import {Badge} from '@/components/ui/badge';
import {Button} from '@/components/ui/button';
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from '@/components/ui/card';
import {Label} from '@/components/ui/label';
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs';
import {Switch} from '@/components/ui/switch';
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,} from '@/components/ui/dialog';
import {InjectsSidebar} from './InjectsSidebar';
import {toast} from 'sonner';
import {resolveMediaUrl} from '@/lib/resolveMediaUrl';
import {
    useDeleteInfrastructureResourceMutation,
    useGetExerciseApproveStateQuery,
    useGetOpenstackFlavorsQuery,
    useGetOpenstackImagesQuery,
    useGetScenarioDetailQuery,
    useSaveInfrastructureConfigurationMutation,
    useSetScenarioApprovalMutation,
    useSetScenarioPreparedMutation,
    useSetScenarioTtxMutation,
} from '@/store/apiSlice';
import {
    Activity,
    AlertCircle,
    Boxes,
    Clock,
    Cloud,
    Cpu,
    File,
    FileText,
    Flag,
    Globe,
    HardDrive,
    Layers,
    Link2,
    ListChecks,
    MemoryStick,
    Network,
    Pencil,
    Plus,
    Router,
    Server,
    Trash2,
} from 'lucide-react';
import type {IExerciseInfrastructure} from '@/types/scenarios';
import {v2Api} from '@/lib/api';
import {useExerciseInfrastructureWebSocket} from '@/hooks/useExerciseInfrastructureWebSocket';
import type {ExerciseFlagMapping} from '@/lib/api/v2';
import {Select} from '@/components/ui/select';
import {AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,} from '@/components/ui/alert-dialog';

interface Props {
    scenarioId: string;
}

function HardwareDetail({icon, label, value,}: {
    icon: React.ReactNode;
    label: string;
    value: string;
}) {
    return (
        <div className="flex items-center space-x-2 p-3 bg-muted/30 rounded-lg">
            <div className="w-5 h-5">{icon}</div>
            <div>
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="font-medium">{value}</p>
            </div>
        </div>
    );
}

function InfraRowActions({
                             onEdit,
                             onDelete,
                             disabled,
                         }: {
    onEdit: () => void;
    onDelete: () => void;
    disabled?: boolean;
}) {
    return (
        <div className="flex items-center justify-end gap-0.5">
            <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                }}
                disabled={disabled}
                title="Edit"
            >
                <Pencil className="h-3.5 w-3.5"/>
            </Button>
            <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive"
                onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                }}
                disabled={disabled}
                title="Delete"
            >
                <Trash2 className="h-3.5 w-3.5"/>
            </Button>
        </div>
    );
}

function formatAllocationPoolsForForm(pools: unknown): string {
    if (!Array.isArray(pools) || pools.length === 0) return '';
    return pools
        .map((p) => {
            if (p && typeof p === 'object' && p !== null && 'start' in p && 'end' in p) {
                const o = p as { start: string; end: string };
                const start = String(o.start ?? '').trim();
                const end = String(o.end ?? '').trim();
                if (start && end) return `${start},${end}`;
            }
            return '';
        })
        .filter(Boolean)
        .join('\n');
}

function parseAllocationPoolsFromForm(text: string): { start: string; end: string }[] {
    const pools: { start: string; end: string }[] = [];
    for (const line of text.split(/\r?\n/)) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        const parts = trimmed.split(',').map((x) => x.trim());
        if (parts.length >= 2 && parts[0] && parts[1]) {
            pools.push({start: parts[0], end: parts[1]});
        }
    }
    return pools;
}

function formatAllocationPoolsTableCell(pools: unknown): string {
    if (!Array.isArray(pools) || pools.length === 0) return '—';
    const bits = pools
        .map((p) => {
            if (p && typeof p === 'object' && p !== null && 'start' in p && 'end' in p) {
                const o = p as { start: string; end: string };
                const start = String(o.start ?? '').trim();
                const end = String(o.end ?? '').trim();
                if (start && end) return `${start}–${end}`;
            }
            return '';
        })
        .filter(Boolean);
    return bits.length ? bits.join('; ') : '—';
}

export function CorporateScenarioPageContent({scenarioId}: Props) {
    useExerciseInfrastructureWebSocket(scenarioId);
    const {
        data: scenario,
        isLoading: loading,
        isFetching,
        error,
        refetch,
    } = useGetScenarioDetailQuery(scenarioId);

    const {data: approveState, isLoading: approveStateLoading} = useGetExerciseApproveStateQuery(scenarioId, {
        skip: !scenarioId,
    });

    const [saveInfrastructureConfiguration, {isLoading: infraSaving}] = useSaveInfrastructureConfigurationMutation();
    const [deleteInfrastructureResource, {isLoading: infraDeleting}] = useDeleteInfrastructureResourceMutation();

    const [infraTab, setInfraTab] = useState<'networks' | 'subnets' | 'routers' | 'instances'>('networks');

    const infra: IExerciseInfrastructure | undefined = scenario?.infrastructure;
    const normalizedScenarioFlags = useMemo<ExerciseFlagMapping[]>(() => {
        const items = scenario?.flags ?? [];
        return items.map((f, idx) => ({
            id: Number(f.map_id ?? 0),
            exercise: String(scenario?.id ?? ''),
            exercise_team: f.exercise_team_mapping?.id ?? null,
            flag: String(f.flag_id ?? ''),
            order: Number(f.order ?? idx + 1),
            is_required: Boolean(f.is_required ?? true),
            flag_question: String(f.question ?? ''),
            flag_answer: String(f.answer ?? ''),
            flag_hint: String(f.hint ?? ''),
            flag_hint_penalty: f.hint_penalty == null ? null : Number(f.hint_penalty),
            flag_score: Number(f.score ?? 0),
            flag_score_type: 'default',
            flag_placeholder: String(f.placeholder ?? ''),
            flag_team: Number(f.team ?? 0),
            flag_team_name: String(f.team_name ?? ''),
            flag_phase: f.phase?.id ?? null,
            flag_phase_name: String(f.phase?.name ?? ''),
        }));
    }, [scenario]);

    const infraResourceTotal = useMemo(() => {
        if (!infra) return 0;
        return (
            infra.networks.length +
            infra.subnets.length +
            infra.routers.length +
            infra.instances.length
        );
    }, [infra]);

    /** Mapping row exists or user has configured cloud / saved resources */
    const infraHasMapping = useMemo(() => {
        if (!infra) return false;
        const ready = infra.is_ready ?? infra.is_active ?? false;
        return Boolean(infra.cloud_name || ready || infraResourceTotal > 0);
    }, [infra, infraResourceTotal]);

    const infraReadyForUi = useMemo(
        () => Boolean(infra?.is_ready ?? infra?.is_active),
        [infra?.is_ready, infra?.is_active]
    );

    const [addNetworkDialogOpen, setAddNetworkDialogOpen] = useState(false);
    const [addSubnetDialogOpen, setAddSubnetDialogOpen] = useState(false);
    const [addRouterDialogOpen, setAddRouterDialogOpen] = useState(false);
    const [addInstanceDialogOpen, setAddInstanceDialogOpen] = useState(false);

    const openstackCatalogArg = useMemo(
        () => ({
            cloudName: infra?.cloud_name || undefined,
        }),
        [infra?.cloud_name]
    );

    const skipOpenstackCatalog = !addInstanceDialogOpen;

    const {
        data: flavorsRes,
        isLoading: flavorsLoading,
        isError: flavorsError,
        refetch: refetchFlavors,
    } = useGetOpenstackFlavorsQuery(openstackCatalogArg, {skip: skipOpenstackCatalog});

    const {
        data: imagesRes,
        isLoading: imagesLoading,
        isError: imagesError,
        refetch: refetchImages,
    } = useGetOpenstackImagesQuery(openstackCatalogArg, {skip: skipOpenstackCatalog});

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
    const [markReadyConfirmOpen, setMarkReadyConfirmOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<{
        kind: 'network' | 'subnet' | 'router' | 'instance';
        id: string;
        name: string;
    } | null>(null);

    const [editingNetworkId, setEditingNetworkId] = useState<string | null>(null);
    const [editingSubnetId, setEditingSubnetId] = useState<string | null>(null);
    const [editingRouterId, setEditingRouterId] = useState<string | null>(null);
    const [editingInstanceId, setEditingInstanceId] = useState<string | null>(null);

    /** Network names from API (for subnet/router dropdowns). */
    const networkNameOptions = useMemo(() => {
        const names = new Set<string>();
        for (const n of infra?.networks ?? []) {
            if (n.name) names.add(n.name);
        }
        return Array.from(names).sort((a, b) => a.localeCompare(b));
    }, [infra?.networks]);

    /** Networks sorted for selects; used to label external vs private for routers. */
    const networksSortedForUi = useMemo(() => {
        return [...(infra?.networks ?? [])].filter((n) => n.name).sort((a, b) => a.name.localeCompare(b.name));
    }, [infra?.networks]);

    /** Active exercise↔team mappings for instance team assignment (ExerciseTeamMapModel rows). */
    const exerciseTeamMappingOptions = useMemo(() => {
        const rows = scenario?.exercise_team_mappings ?? [];
        return rows.filter((m) => m.is_active).sort((a, b) => a.team_name.localeCompare(b.team_name));
    }, [scenario?.exercise_team_mappings]);

    const [networkDraft, setNetworkDraft] = useState({
        name: '',
        is_external: false,
        is_shared: false,
        mtu: 1400,
        description: '',
        admin_state_up: true,
    });

    const [subnetDraft, setSubnetDraft] = useState({
        name: '',
        network: '',
        cidr: '',
        gateway_ip: '',
        ip_version: 4,
        enable_dhcp: true,
        dns_preset: 'google' as 'google' | 'none',
        /** One pool per line: `start_ip,end_ip` (comma-separated). */
        allocation_pools_text: '',
    });

    const [routerDraft, setRouterDraft] = useState({
        name: '',
        /** Network names selected for this router (multi). */
        selectedNetworkNames: [] as string[],
        /** Neutron external gateway flag (public network is resolved on the server). */
        is_external_gateway: true,
        interfacesText: '',
        enable_snat: true,
        admin_state_up: true,
        description: '',
    });

    /** Add-router dialog: keep gateway on unless the row explicitly has it off (`false`). */
    useEffect(() => {
        if (!addRouterDialogOpen || editingRouterId != null) return;
        setRouterDraft((d) => ({...d, is_external_gateway: true}));
    }, [addRouterDialogOpen, editingRouterId]);

    const [instanceDraft, setInstanceDraft] = useState({
        name: '',
        image: '',
        flavor: '',
    });

    const flavorSelectOptions = useMemo(() => {
        const rows = flavorsRes?.flavors ?? [];
        const name = instanceDraft.flavor.trim();
        if (name && !rows.some((r) => r.name === name)) {
            return [...rows, {id: `legacy:${name}`, name}];
        }
        return rows;
    }, [flavorsRes?.flavors, instanceDraft.flavor]);

    const imageSelectOptions = useMemo(() => {
        const rows = imagesRes?.images ?? [];
        const name = instanceDraft.image.trim();
        if (name && !rows.some((r) => r.name === name)) {
            return [...rows, {id: `legacy:${name}`, name}];
        }
        return rows;
    }, [imagesRes?.images, instanceDraft.image]);

    /** Selected networks + optional static (fixed) IP per network for the instance dialog */
    const [instanceNetworkAttachments, setInstanceNetworkAttachments] = useState<
        Array<{ network: string; fixed_ip: string }>
    >([]);

    /** exercise_team_mappings[].id — which team this instance belongs to (ExerciseInstanceTeamMapModel target). */
    const [instanceExerciseTeamMappingId, setInstanceExerciseTeamMappingId] = useState<number | ''>('');
    const infraLockedByActiveRun = Boolean(scenario?.has_active_run);
    const infraActionDisabled = infraDeleting || infraSaving || infraLockedByActiveRun;

    const toggleInstanceNetwork = useCallback((name: string) => {
        setInstanceNetworkAttachments((prev) => {
            const idx = prev.findIndex((x) => x.network === name);
            if (idx >= 0) return prev.filter((_, i) => i !== idx);
            return [...prev, {network: name, fixed_ip: ''}].sort((a, b) => a.network.localeCompare(b.network));
        });
    }, []);

    const setInstanceNetworkFixedIp = useCallback((network: string, fixed_ip: string) => {
        setInstanceNetworkAttachments((prev) =>
            prev.map((x) => (x.network === network ? {...x, fixed_ip} : x))
        );
    }, []);

    const [setScenarioApproval, {isLoading: approvalUpdating}] = useSetScenarioApprovalMutation();
    const [setScenarioPrepared, {isLoading: preparedUpdating}] = useSetScenarioPreparedMutation();
    const [setScenarioTtx, {isLoading: ttxUpdating}] = useSetScenarioTtxMutation();
    const [activeTab, setActiveTab] = useState<string>('description');
    const [pageTab, setPageTab] = useState<'details' | 'infrastructure'>('details');

    const [showInjectsSidebar, setShowInjectsSidebar] = useState(false);
    const [sidebarPanel, setSidebarPanel] = useState<'injects' | 'exercise-phases' | 'team-mappings' | null>(null);
    const [phaseCatalog, setPhaseCatalog] = useState<Array<{ id: number; name: string; slug: string }>>([]);
    const allPhases = useMemo(() => {
        const byId = new Map<number, { id: number; name: string; slug: string }>();
        for (const p of scenario?.phases ?? []) byId.set(p.id, {id: p.id, name: p.name, slug: p.slug});
        for (const p of phaseCatalog) {
            if (!byId.has(p.id)) byId.set(p.id, p);
        }
        return Array.from(byId.values());
    }, [scenario?.phases, phaseCatalog]);
    const phaseNameById = useMemo(() => new Map(allPhases.map((p) => [p.id, p.name])), [allPhases]);

    useEffect(() => {
        let mounted = true;
        const loadPhases = async () => {
            if ((scenario?.phases ?? []).length > 0) return;
            try {
                const list = await v2Api.dashboard.phases.list();
                if (!mounted) return;
                setPhaseCatalog(list.map((p) => ({id: p.id, name: p.name, slug: p.slug})));
            } catch {
                if (mounted) setPhaseCatalog([]);
            }
        };
        void loadPhases();
        return () => {
            mounted = false;
        };
    }, [scenario?.phases]);

    const openInjectsSidebar = () => {
        setSidebarPanel('injects');
        setShowInjectsSidebar(true);
    };
    const openExercisePhasesSidebar = () => {
        setSidebarPanel('exercise-phases');
        setShowInjectsSidebar(true);
    };
    const openTeamMappingsSidebar = () => {
        setSidebarPanel('team-mappings');
        setShowInjectsSidebar(true);
    };
    const closeInjectsSidebar = () => {
        setShowInjectsSidebar(false);
        setSidebarPanel(null);
    };


    const fetchScenario = useCallback(async () => {
        await refetch();
    }, [refetch]);

    const handleConfirmDelete = useCallback(async () => {
        if (!deleteTarget || !scenarioId) return;
        if (infraLockedByActiveRun) {
            toast.error('Infrastructure is locked while a scenario run is active.');
            return;
        }
        try {
            await deleteInfrastructureResource({
                exercise_id: scenarioId,
                resource_type: deleteTarget.kind,
                resource_id: deleteTarget.id,
            }).unwrap();
            toast.success('Resource deleted.');
            setDeleteOpen(false);
            setDeleteTarget(null);
            await refetch();
        } catch (err: any) {
            toast.error(err?.data?.detail ?? 'Delete failed.');
        }
    }, [deleteInfrastructureResource, deleteTarget, infraLockedByActiveRun, refetch, scenarioId]);

    const canShowApprovalButton = Boolean(scenario?.is_prepared);
    const canStartScenario = Boolean(scenario?.is_prepared && scenario?.is_approved);
    /** Backend prepare validation (infra, teams, flags, walkthroughs) — same as PATCH /prepare. */
    const canApproveNow = Boolean(approveState?.can_approve);

    const handleApprove = async () => {
        if (!scenario?.id || approvalUpdating) return;
        try {
            await setScenarioApproval({id: scenario.id, is_approved: true}).unwrap();
            await fetchScenario();
            toast.success('Scenario approved.');
        } catch (err) {
            const anyErr = err as { data?: { detail?: string } };
            toast.error(anyErr?.data?.detail ?? 'Failed to approve scenario.');
            console.error('Failed to approve scenario:', err);
        }
    };

    const handlePrepare = async () => {
        if (!scenario?.id || preparedUpdating) return;
        try {
            await setScenarioPrepared({id: scenario.id, is_prepared: true}).unwrap();
            await fetchScenario();
            toast.success('Scenario marked ready for publish.');
        } catch (err) {
            const anyErr = err as unknown as {
                data?: { detail?: string };
                error?: string;
            };
            const detail =
                anyErr?.data?.detail || anyErr?.error || (err instanceof Error ? err.message : null);
            toast.error(detail ?? 'Failed to mark scenario as ready for publish.');
            console.error('Failed to prepare scenario:', err);
        }
    };

    const handleUnapprove = async () => {
        if (!scenario?.id || approvalUpdating) return;
        try {
            await setScenarioApproval({id: scenario.id, is_approved: false}).unwrap();
            await fetchScenario();
            toast.success('Approval revoked.');
        } catch (err) {
            const anyErr = err as { data?: { detail?: string } };
            toast.error(anyErr?.data?.detail ?? 'Failed to revoke approval.');
            console.error('Failed to unapprove scenario:', err);
        }
    };

    const handleTtxToggle = async () => {
        if (!scenario || ttxUpdating) return;
        const next = !scenario.ttx_enabled;
        try {
            await setScenarioTtx({id: scenarioId, ttx_enabled: next}).unwrap();
        } catch (err) {
            console.error('Failed to update TTX:', err);
        }
    };

    let errorMessage: string | null = null;
    if (error) {
        const err = error as { status?: number; data?: { detail?: string } };
        if (err.status === 404) errorMessage = 'Scenario not found';
        else if (err.status === 401 || err.status === 403) errorMessage = "You don't have permission to view this scenario";
        else errorMessage = err.data?.detail || 'Failed to load scenario';
    }

    if (loading || isFetching) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"/>
                <span className="ml-3">Loading scenario details...</span>
            </div>
        );
    }

    if (errorMessage || !scenario) {
        return (
            <div className="space-y-4">
                <Link to="/scenarios">
                    <Button variant="ghost">← Back to Scenarios</Button>
                </Link>
                <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
                    <p className="font-medium text-destructive">{errorMessage ?? 'Scenario not found'}</p>
                    <p className="text-sm text-muted-foreground mt-1">Please check the URL or go back to the list.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8 py-6">
            <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="relative w-full md:w-1/3 lg:w-1/4 aspect-video rounded-xl overflow-hidden shadow-md border border-border">
                    <img
                        src={resolveMediaUrl(scenario.thumbnail_url) || undefined}
                        alt={scenario.name}
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="flex-1 space-y-4">
                    <div className="flex flex-col space-y-2">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <h1 className="text-3xl font-bold tracking-tight">{scenario.name}</h1>
                            <div className="flex items-center gap-2 flex-wrap">
                                <Button
                                    variant={scenario.ttx_enabled ? 'secondary' : 'default'}
                                    size="default"
                                    onClick={handleTtxToggle}
                                    disabled={ttxUpdating}
                                    className={scenario.ttx_enabled ? '' : 'bg-amber-600 hover:bg-amber-700'}
                                >
                                    {ttxUpdating
                                        ? 'Updating…'
                                        : scenario.ttx_enabled
                                            ? 'Disable TTX'
                                            : 'Enable TTX'}
                                </Button>
                                {scenario.ttx_enabled && (
                                    <Button
                                        variant="outline"
                                        size="default"
                                        onClick={openInjectsSidebar}
                                    >
                                        <ListChecks className="mr-2 h-4 w-4"/>
                                        View Injects
                                    </Button>
                                )}
                                <Button
                                    variant="outline"
                                    size="default"
                                    onClick={openExercisePhasesSidebar}
                                >
                                    <Layers className="mr-2 h-4 w-4"/>
                                    Exercise Phases
                                </Button>
                                <Button
                                    variant="outline"
                                    size="default"
                                    onClick={openTeamMappingsSidebar}
                                >
                                    <Link2 className="mr-2 h-4 w-4"/>
                                    Team Mappings
                                </Button>
                                {canShowApprovalButton && (
                                    scenario.is_approved ? (
                                        <Button
                                            variant="destructive"
                                            size="default"
                                            onClick={handleUnapprove}
                                            disabled={approvalUpdating || infraLockedByActiveRun}
                                            title={
                                                infraLockedByActiveRun
                                                    ? 'Cannot unpublish while a scenario run is active.'
                                                    : undefined
                                            }
                                            className="bg-red-600 hover:bg-red-700 text-white"

                                        >
                                            {approvalUpdating ? 'Processing...' : 'Unpublish'}
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="default"
                                            size="default"
                                            onClick={() => setPublishConfirmOpen(true)}
                                            disabled={
                                                approvalUpdating ||
                                                approveStateLoading ||
                                                !canApproveNow ||
                                                infraLockedByActiveRun
                                            }
                                            title={
                                                infraLockedByActiveRun
                                                    ? 'Cannot change publish state while a scenario run is active.'
                                                    : !canApproveNow && approveState?.prepare_validation_error
                                                        ? approveState.prepare_validation_error
                                                        : undefined
                                            }
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                        >
                                            {approvalUpdating
                                                ? 'Processing...'
                                                : approveStateLoading
                                                    ? 'Checking…'
                                                    : 'Published'}
                                        </Button>
                                    )
                                )}
                                {!canShowApprovalButton && typeof scenario?.is_prepared === 'boolean' && (
                                    <Button
                                        variant="default"
                                        size="default"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setMarkReadyConfirmOpen(true);
                                        }}
                                        disabled={preparedUpdating}
                                        className="bg-blue-600 hover:bg-blue-700 text-white"
                                    >
                                        {preparedUpdating ? 'Marking ready for publish...' : 'Ready for publish'}
                                    </Button>
                                )}
                                {canStartScenario && (
                                    <CorporateScenarioContent
                                        scenarioID={scenario.id}
                                        scenarioName={scenario.name}
                                        machines={scenario.machine_names || []}
                                        inlineLayout
                                    />
                                )}
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="secondary" className="font-normal">
                                {scenario.category.name}
                            </Badge>
                            <Badge
                                variant={
                                    scenario.severity === 'Very Easy' || scenario.severity === 'Easy'
                                        ? 'default'
                                        : scenario.severity === 'Medium'
                                            ? 'secondary'
                                            : 'destructive'
                                }
                            >
                                {scenario.severity}
                            </Badge>
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Clock className="w-4 h-4 mr-1"/>
                                <span>Points: {scenario.points}</span>
                            </div>
                            {typeof scenario.ttx_enabled === 'boolean' && (
                                <Badge
                                    className={
                                        scenario.ttx_enabled
                                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                                            : 'bg-muted text-muted-foreground'
                                    }
                                >
                                    TTX {scenario.ttx_enabled ? 'On' : 'Off'}
                                </Badge>
                            )}
                            {typeof scenario.is_prepared === 'boolean' && (
                                <Badge
                                    className={
                                        scenario.is_prepared
                                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                                            : 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300'
                                    }
                                >
                                    {scenario.is_prepared ? 'Prepared' : 'Not Prepared'}
                                </Badge>
                            )}
                        </div>
                        <p className="text-muted-foreground">
                            Created by <span className="font-medium text-foreground">{scenario.creator?.full_name || 'N/A'}</span>
                        </p>
                    </div>
                    {scenario.hardware_details && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2">
                            <HardwareDetail
                                icon={<Cpu className="text-blue-500"/>}
                                label="vCPU"
                                value={`${scenario.hardware_details.vcpu ?? '-'} Cores`}
                            />
                            <HardwareDetail
                                icon={<MemoryStick className="text-purple-500"/>}
                                label="Memory"
                                value={scenario.hardware_details.ram_gb != null ? `${scenario.hardware_details.ram_gb} GB` : '-'}
                            />
                            <HardwareDetail
                                icon={<HardDrive className="text-green-500"/>}
                                label="Storage"
                                value={scenario.hardware_details.disk_size_gb != null ? `${scenario.hardware_details.disk_size_gb} GB` : '-'}
                            />
                            <HardwareDetail
                                icon={<Server className="text-amber-500"/>}
                                label="VMs"
                                value={String(scenario.hardware_details.vm_count ?? 0)}
                            />
                        </div>
                    )}
                </div>
            </div>

            <Tabs
                value={pageTab}
                onValueChange={(v) => setPageTab(v as 'details' | 'infrastructure')}
                className="w-full"
            >
                <TabsList className="w-full justify-start rounded-lg bg-muted/50 p-1 h-auto gap-1 flex-wrap sm:w-auto">
                    <TabsTrigger value="details" className="gap-2">
                        <FileText className="h-4 w-4 shrink-0"/>
                        Overview
                    </TabsTrigger>
                    <TabsTrigger value="infrastructure" className="gap-2">
                        <Layers className="h-4 w-4 shrink-0"/>
                        Infrastructure
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="mt-6 space-y-0 outline-none">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2 space-y-6">
                            <div className={`flex flex-col gap-5`}>
                                <Card>
                                    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                                        <TabsList className="w-full justify-start rounded-none rounded-t-lg bg-muted/50 p-0 h-auto">
                                            {(['description', 'objective', 'prerequisite'] as const).map((tab) => (
                                                <TabsTrigger
                                                    key={tab}
                                                    value={tab}
                                                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                                                >
                                                    {tab === 'description' && <FileText className="w-4 h-4 mr-2"/>}
                                                    {tab === 'objective' && <Flag className="w-4 h-4 mr-2"/>}
                                                    {tab === 'prerequisite' && <AlertCircle className="w-4 h-4 mr-2"/>}
                                                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                                </TabsTrigger>
                                            ))}
                                            {typeof scenario.ttx_enabled === 'boolean' && scenario.ttx_enabled && (
                                                <TabsTrigger
                                                    value="injects"
                                                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                                                >
                                                    <ListChecks className="w-4 h-4 mr-2"/>
                                                    Injects
                                                </TabsTrigger>
                                            )}
                                        </TabsList>
                                        <div className="p-6 overflow-hidden max-h-[550px] overflow-y-auto">
                                            <TabsContent value="description" className="prose prose-sm max-w-none mt-0">
                                                <div dangerouslySetInnerHTML={{__html: scenario.description}}/>
                                            </TabsContent>
                                            <TabsContent value="objective" className="prose prose-sm max-w-none mt-0">
                                                <div dangerouslySetInnerHTML={{__html: scenario.objective}}/>
                                            </TabsContent>
                                            <TabsContent value="prerequisite" className="prose prose-sm max-w-none mt-0">
                                                <div dangerouslySetInnerHTML={{__html: scenario.prerequisite}}/>
                                            </TabsContent>
                                            {typeof scenario.ttx_enabled === 'boolean' && scenario.ttx_enabled && (
                                                <TabsContent value="injects" className="mt-0">
                                                    <ScenarioInjectsTab scenarioId={scenarioId}/>
                                                </TabsContent>
                                            )}
                                        </div>
                                    </Tabs>
                                </Card>
                            </div>
                            {(scenario.machine_names?.length ?? 0) > 0 && (
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Virtual Machines</CardTitle>
                                        <CardDescription>Machines included in this scenario</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid gap-4">
                                            {(scenario.machine_names ?? []).map((machine, idx) => (
                                                <div
                                                    key={idx}
                                                    className="flex items-center p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                                                >
                                                    <div className="flex-1">
                                                        <p className="font-medium">{machine}</p>
                                                    </div>
                                                    <Button variant="outline" size="sm">
                                                        Connect
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </div>

                        <div className="space-y-6">


                            <ExerciseFlagsCard
                                exerciseId={scenario.id}
                                title="Team Flags"
                                initialFlags={normalizedScenarioFlags}
                                initialMappings={scenario.exercise_team_mappings ?? []}
                                exercisePhases={(scenario.exercise_phases ?? []).map((p) => ({
                                    value: p.phase,
                                    label: phaseNameById.get(p.phase) ?? `Phase ${p.phase}`,
                                }))}
                                onRefetch={fetchScenario}
                            />
                            {scenario.files_data && (
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Team Files</CardTitle>
                                        <CardDescription>Resources available per team</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-4">
                                            {Object.entries(scenario.files_data).map(([team, files]) => (
                                                <div key={team} className="space-y-2">
                                                    <div className="flex items-center space-x-2">
                                                        <div
                                                            className={`w-2 h-2 rounded-full ${
                                                                team === 'red_team'
                                                                    ? 'bg-red-500'
                                                                    : team === 'blue_team'
                                                                        ? 'bg-blue-500'
                                                                        : team === 'purple_team'
                                                                            ? 'bg-purple-500'
                                                                            : 'bg-yellow-500'
                                                            }`}
                                                        />
                                                        <h4 className="text-sm font-medium capitalize">{team.replace('_', ' ')}</h4>
                                                    </div>
                                                    {files && files.length > 0 ? (
                                                        <ul className="flex gap-4 pl-3 flex-wrap">
                                                            {files.map((file, idx) => (
                                                                <li key={idx}>
                                                                    <a
                                                                        href={file}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="flex flex-col items-center gap-2.5 border px-5 py-2.5 rounded-md text-sm text-muted-foreground hover:text-foreground"
                                                                    >
                                                                        <File className="h-5 w-5"/>
                                                                        PDF
                                                                    </a>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <p className="text-sm text-muted-foreground pl-4">No files available</p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                            <WalkthroughsCard
                                exerciseId={scenario.id}
                                title="Walkthrough Files"
                                initialItems={scenario.walkthrough_files}
                                initialMappings={scenario.exercise_team_mappings}
                                onRefetch={fetchScenario}
                            />
                        </div>
                    </div>

                </TabsContent>

                <TabsContent value="infrastructure" className="mt-6 outline-none">
                    <Card className="overflow-hidden border-border/80 shadow-sm">
                        <CardHeader className="space-y-1 border-b border-border/60 bg-muted/30 pb-4">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                <div className="min-w-0 space-y-1">
                                    <CardTitle className="flex items-center gap-2 text-lg font-semibold tracking-tight">
                                        <Layers className="h-5 w-5 text-primary shrink-0"/>
                                        Infrastructure
                                    </CardTitle>
                                    <CardDescription className="text-sm leading-relaxed">
                                        Create and manage networks, subnets, routers, and instances for this exercise.
                                    </CardDescription>
                                </div>
                                {infraHasMapping && (
                                    <div className="flex shrink-0 flex-wrap items-center justify-end gap-x-4 gap-y-1 text-sm text-foreground sm:text-right">
                        <span className="inline-flex items-center gap-1.5">
                          <Cloud className="h-4 w-4 text-muted-foreground shrink-0"/>
                          <span className="text-muted-foreground">Cloud:</span>{' '}
                            <span className="font-medium">{infra?.cloud_name || '—'}</span>
                        </span>
                                        <span className="inline-flex items-center gap-1.5">
                          <Activity className="h-4 w-4 text-muted-foreground shrink-0"/>
                          <span className="text-muted-foreground">Ready:</span>{' '}
                                            <span className="font-medium">{infraReadyForUi ? 'Yes' : 'No'}</span>
                        </span>
                                    </div>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent className="pt-5">
                            <div className="space-y-5">
                                {infraLockedByActiveRun && (
                                    <div
                                        className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
                                        Infrastructure is locked because at least one scenario run is active
                                        (launching, launched, started, or paused). End active runs to modify
                                        networks, subnets, routers, or instances.
                                    </div>
                                )}
                                {(loading && !scenario) || !infraHasMapping ? (
                                    <div className="text-sm text-muted-foreground">
                                        {loading && !scenario ? (
                                            <>Loading scenario & infrastructure...</>
                                        ) : (
                                            <>No infrastructure mapping yet — add resources below to create one.</>
                                        )}
                                    </div>
                                ) : null}

                                <Tabs
                                    value={infraTab}
                                    onValueChange={(v) => setInfraTab(v as typeof infraTab)}
                                    className="w-full"
                                >
                                    <TabsList className="w-full justify-start flex-wrap rounded-none bg-muted/50 h-auto gap-0">
                                        <TabsTrigger value="networks" className="rounded-none gap-1.5">
                                            <Network className="h-4 w-4 shrink-0"/>
                                            <span>Networks</span>
                                            <span className="ml-0.5 rounded-md bg-background/80 px-1.5 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
                            {infra?.networks.length ?? 0}
                          </span>
                                        </TabsTrigger>
                                        <TabsTrigger value="subnets" className="rounded-none gap-1.5">
                                            <Boxes className="h-4 w-4 shrink-0"/>
                                            <span>Subnets</span>
                                            <span className="ml-0.5 rounded-md bg-background/80 px-1.5 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
                            {infra?.subnets.length ?? 0}
                          </span>
                                        </TabsTrigger>
                                        <TabsTrigger value="routers" className="rounded-none gap-1.5">
                                            <Router className="h-4 w-4 shrink-0"/>
                                            <span>Routers</span>
                                            <span className="ml-0.5 rounded-md bg-background/80 px-1.5 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
                            {infra?.routers.length ?? 0}
                          </span>
                                        </TabsTrigger>
                                        <TabsTrigger value="instances" className="rounded-none gap-1.5">
                                            <Server className="h-4 w-4 shrink-0"/>
                                            <span>Instances</span>
                                            <span className="ml-0.5 rounded-md bg-background/80 px-1.5 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
                            {infra?.instances.length ?? 0}
                          </span>
                                        </TabsTrigger>
                                    </TabsList>

                                    <div className="rounded-b-lg border border-t-0 border-border/60 bg-muted/10 p-4 pt-3">
                                        <TabsContent value="networks">
                                            <div className="space-y-5">
                                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                                    <div>
                                                        <h3 className="text-base font-semibold text-foreground">Networks</h3>
                                                        <p className="text-sm text-muted-foreground">
                                                            Virtual networks for this exercise.
                                                        </p>
                                                    </div>
                                                    <Button
                                                        className="shrink-0 gap-2"
                                                        disabled={infraActionDisabled}
                                                        onClick={() => {
                                                            setEditingNetworkId(null);
                                                            setNetworkDraft({
                                                                name: '',
                                                                is_external: false,
                                                                is_shared: false,
                                                                mtu: 1400,
                                                                description: '',
                                                                admin_state_up: true,
                                                            });
                                                            setAddNetworkDialogOpen(true);
                                                        }}
                                                    >
                                                        <Plus className="h-4 w-4"/>
                                                        Add new
                                                    </Button>
                                                </div>

                                                {infra && infra.networks.length > 0 ? (
                                                    <div className="overflow-x-auto rounded-lg border border-border/60 bg-background/50 shadow-sm">
                                                        <table className="w-full text-xs">
                                                            <thead className="bg-muted/60">
                                                            <tr className="text-left">
                                                                <th className="p-2.5 font-medium">Name</th>
                                                                <th className="p-2.5 font-medium">MTU</th>
                                                                <th className="p-2.5 font-medium">External</th>
                                                                <th className="p-2.5 font-medium">State</th>
                                                                <th className="p-2.5 font-medium w-[100px] text-right">Actions</th>
                                                            </tr>
                                                            </thead>
                                                            <tbody>
                                                            {infra.networks.map((n) => (
                                                                <tr key={n.id} className="border-t border-border/60 hover:bg-muted/30">
                                                                    <td className="p-2.5 font-medium text-foreground">{n.name}</td>
                                                                    <td className="p-2.5">{n.mtu ?? '—'}</td>
                                                                    <td className="p-2.5">{n.is_external ? 'Yes' : 'No'}</td>
                                                                    <td className="p-2.5 capitalize">{n.lifecycle_state ?? '—'}</td>
                                                                    <td className="p-2.5">
                                                                        <InfraRowActions
                                                                            disabled={infraActionDisabled}
                                                                            onEdit={() => {
                                                                                setEditingNetworkId(n.id);
                                                                                setNetworkDraft({
                                                                                    name: n.name,
                                                                                    is_external: Boolean(n.is_external),
                                                                                    is_shared: Boolean(n.is_shared),
                                                                                    mtu: n.mtu ?? 1400,
                                                                                    description: n.description ?? '',
                                                                                    admin_state_up: n.admin_state_up ?? true,
                                                                                });
                                                                                setAddNetworkDialogOpen(true);
                                                                            }}
                                                                            onDelete={() => {
                                                                                setDeleteTarget({kind: 'network', id: n.id, name: n.name});
                                                                                setDeleteOpen(true);
                                                                            }}
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="rounded-lg border border-dashed border-border/60 bg-muted/20 px-4 py-10 text-center text-sm text-muted-foreground">
                                                        No networks yet. Click <span className="font-medium text-foreground">Add new</span> to
                                                        create one.
                                                    </div>
                                                )}

                                                <Dialog
                                                    open={addNetworkDialogOpen}
                                                    onOpenChange={(open) => {
                                                        setAddNetworkDialogOpen(open);
                                                        if (!open) setEditingNetworkId(null);
                                                    }}
                                                >
                                                    <DialogContent>
                                                        <DialogHeader>
                                                            <DialogTitle className="flex items-center gap-2">
                                                                <Network className="h-5 w-5 text-muted-foreground"/>
                                                                {editingNetworkId ? 'Edit network' : 'Add network'}
                                                            </DialogTitle>
                                                            <DialogDescription>Save network configuration for this exercise.</DialogDescription>
                                                        </DialogHeader>

                                                        <div className="space-y-3">
                                                            <div className="grid grid-cols-1 md:grid-cols-1 gap-3">
                                                                <div className="space-y-1">
                                                                    <Label>Network name</Label>
                                                                    <input
                                                                        value={networkDraft.name}
                                                                        onChange={(e) => setNetworkDraft((d) => ({...d, name: e.target.value}))}
                                                                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                                                                        placeholder="net-backend"
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                                <div className="space-y-1">
                                                                    <Label>MTU</Label>
                                                                    <input
                                                                        type="number"
                                                                        value={networkDraft.mtu}
                                                                        onChange={(e) =>
                                                                            setNetworkDraft((d) => ({...d, mtu: Number(e.target.value || 0)}))
                                                                        }
                                                                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary cursor-not-allowed"
                                                                        disabled={true}
                                                                    />
                                                                </div>
                                                                <div className="space-y-1">
                                                                    <Label>Description</Label>
                                                                    <input
                                                                        value={networkDraft.description}
                                                                        onChange={(e) =>
                                                                            setNetworkDraft((d) => ({...d, description: e.target.value}))
                                                                        }
                                                                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                                                                        placeholder="Optional description"
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                                <div className="flex items-center justify-between gap-2">
                                                                    <span className="text-sm text-muted-foreground">Shared</span>
                                                                    <Switch
                                                                        checked={networkDraft.is_shared}
                                                                        onCheckedChange={(v: boolean) =>
                                                                            setNetworkDraft((d) => ({...d, is_shared: v}))
                                                                        }
                                                                    />
                                                                </div>
                                                                <div className="flex items-center justify-between gap-2">
                                                                    <span className="text-sm text-muted-foreground">Admin Up</span>
                                                                    <Switch
                                                                        checked={networkDraft.admin_state_up}
                                                                        onCheckedChange={(v: boolean) =>
                                                                            setNetworkDraft((d) => ({...d, admin_state_up: v}))
                                                                        }
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <DialogFooter>
                                                            <Button variant="outline" onClick={() => setAddNetworkDialogOpen(false)}>
                                                                Cancel
                                                            </Button>
                                                            <Button
                                                                disabled={infraActionDisabled}
                                                                onClick={async () => {
                                                                    if (!scenarioId) return;
                                                                    if (!networkDraft.name.trim()) {
                                                                        toast.error('Network name is required.');
                                                                        return;
                                                                    }
                                                                    if (!networkDraft.mtu || networkDraft.mtu < 576) {
                                                                        toast.error('MTU must be >= 576.');
                                                                        return;
                                                                    }

                                                                    const networkSpec = {
                                                                        ...networkDraft,
                                                                        mtu: Number(networkDraft.mtu),
                                                                        ...(editingNetworkId ? {id: editingNetworkId} : {}),
                                                                    };

                                                                    try {
                                                                        await saveInfrastructureConfiguration({
                                                                            exercise_id: scenarioId,
                                                                            networks: [networkSpec],
                                                                        }).unwrap();
                                                                        toast.success(editingNetworkId ? 'Network updated.' : 'Network saved.');
                                                                        await refetch();
                                                                    } catch (err: any) {
                                                                        const detail =
                                                                            err?.data?.detail ??
                                                                            err?.error ??
                                                                            'Failed to save network configuration.';
                                                                        toast.error(detail);
                                                                        console.error('Save network failed:', err);
                                                                        return;
                                                                    }

                                                                    setNetworkDraft({
                                                                        name: '',
                                                                        is_external: false,
                                                                        is_shared: false,
                                                                        mtu: 1400,
                                                                        description: '',
                                                                        admin_state_up: true,
                                                                    });
                                                                    setEditingNetworkId(null);
                                                                    setAddNetworkDialogOpen(false);
                                                                }}
                                                            >
                                                                {editingNetworkId ? 'Save changes' : 'Add'}
                                                            </Button>
                                                        </DialogFooter>
                                                    </DialogContent>
                                                </Dialog>
                                            </div>
                                        </TabsContent>

                                        <TabsContent value="subnets">
                                            <div className="space-y-5">
                                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                                    <div>
                                                        <h3 className="text-base font-semibold text-foreground">Subnets</h3>
                                                        <p className="text-sm text-muted-foreground">
                                                            Subnets attached to your networks (CIDR, gateway, DHCP, optional allocation pools).
                                                        </p>
                                                    </div>
                                                    <Button
                                                        className="shrink-0 gap-2"
                                                        disabled={infraActionDisabled}
                                                        onClick={() => {
                                                            setEditingSubnetId(null);
                                                            setSubnetDraft({
                                                                name: '',
                                                                network: '',
                                                                cidr: '',
                                                                gateway_ip: '',
                                                                ip_version: 4,
                                                                enable_dhcp: true,
                                                                dns_preset: 'google',
                                                                allocation_pools_text: '',
                                                            });
                                                            setAddSubnetDialogOpen(true);
                                                        }}
                                                    >
                                                        <Plus className="h-4 w-4"/>
                                                        Add new
                                                    </Button>
                                                </div>

                                                {infra && infra.subnets.length > 0 && (
                                                    <div className="space-y-2">
                                                        <div className="overflow-x-auto rounded-lg border border-border/60 bg-background/50 shadow-sm">
                                                            <table className="w-full text-xs">
                                                                <thead className="bg-muted/60">
                                                                <tr className="text-left">
                                                                    <th className="p-2.5 font-medium">Name</th>
                                                                    <th className="p-2.5 font-medium">Network</th>
                                                                    <th className="p-2.5 font-medium">CIDR</th>
                                                                    <th className="p-2.5 font-medium">Allocation pools</th>
                                                                    <th className="p-2.5 font-medium">State</th>
                                                                    <th className="p-2.5 font-medium w-[100px] text-right">Actions</th>
                                                                </tr>
                                                                </thead>
                                                                <tbody>
                                                                {infra.subnets.map((s) => (
                                                                    <tr key={s.id} className="border-t border-border/60 hover:bg-muted/30">
                                                                        <td className="p-2.5 font-medium text-foreground">{s.name}</td>
                                                                        <td className="p-2.5">{s.network_name || '—'}</td>
                                                                        <td className="p-2.5 font-mono">{s.cidr || '—'}</td>
                                                                        <td className="p-2.5 max-w-[220px] break-words text-muted-foreground">
                                                                            {formatAllocationPoolsTableCell(s.allocation_pools)}
                                                                        </td>
                                                                        <td className="p-2.5 capitalize">{s.lifecycle_state ?? '—'}</td>
                                                                        <td className="p-2.5">
                                                                            <InfraRowActions
                                                                                disabled={infraActionDisabled}
                                                                                onEdit={() => {
                                                                                    setEditingSubnetId(s.id);
                                                                                    const dns = s.dns_nameservers ?? [];
                                                                                    const looksLikeGoogle =
                                                                                        dns.length === 2 &&
                                                                                        dns.includes('8.8.8.8') &&
                                                                                        dns.includes('8.8.4.4');
                                                                                    setSubnetDraft({
                                                                                        name: s.name,
                                                                                        network: s.network_name || '',
                                                                                        cidr: s.cidr || '',
                                                                                        gateway_ip: s.gateway_ip || '',
                                                                                        ip_version: s.ip_version ?? 4,
                                                                                        enable_dhcp: s.enable_dhcp ?? true,
                                                                                        dns_preset: looksLikeGoogle || dns.length === 0 ? 'google' : 'none',
                                                                                        allocation_pools_text: formatAllocationPoolsForForm(s.allocation_pools),
                                                                                    });
                                                                                    setAddSubnetDialogOpen(true);
                                                                                }}
                                                                                onDelete={() => {
                                                                                    setDeleteTarget({kind: 'subnet', id: s.id, name: s.name});
                                                                                    setDeleteOpen(true);
                                                                                }}
                                                                            />
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}

                                                {infra && infra.subnets.length === 0 && (
                                                    <div
                                                        className="rounded-lg border border-dashed border-border/60 bg-muted/20 px-4 py-10 text-center text-sm text-muted-foreground">
                                                        No subnets yet. Click Add new to create one (requires at least one network).
                                                    </div>
                                                )}

                                                <Dialog
                                                    open={addSubnetDialogOpen}
                                                    onOpenChange={(open) => {
                                                        setAddSubnetDialogOpen(open);
                                                        if (!open) setEditingSubnetId(null);
                                                    }}
                                                >
                                                    <DialogContent>
                                                        <DialogHeader>
                                                            <DialogTitle className="flex items-center gap-2">
                                                                <Boxes className="h-5 w-5 text-muted-foreground"/>
                                                                {editingSubnetId ? 'Edit subnet' : 'Add subnet'}
                                                            </DialogTitle>
                                                            <DialogDescription>Save subnet configuration for this exercise.</DialogDescription>
                                                        </DialogHeader>

                                                        <div className="space-y-3">
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                                <div className="space-y-1">
                                                                    <Label className="flex items-center gap-2">
                                                                        <Boxes className="h-4 w-4 text-muted-foreground"/>
                                                                        Subnet name
                                                                    </Label>
                                                                    <input
                                                                        value={subnetDraft.name}
                                                                        onChange={(e) => setSubnetDraft((d) => ({...d, name: e.target.value}))}
                                                                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                                                                        placeholder="sub-backend"
                                                                    />
                                                                </div>
                                                                <div className="space-y-1">
                                                                    <Label className="flex items-center gap-2">
                                                                        <Network className="h-4 w-4 text-muted-foreground"/>
                                                                        Network
                                                                    </Label>
                                                                    {networkNameOptions.length === 0 ? (
                                                                        <>
                                                                            <p className="text-xs text-amber-600 dark:text-amber-500">
                                                                                Add and save at least one network before creating a subnet.
                                                                            </p>
                                                                            <Select disabled value="">
                                                                                <option value="">No networks available</option>
                                                                            </Select>
                                                                        </>
                                                                    ) : (
                                                                        <Select
                                                                            value={subnetDraft.network}
                                                                            onChange={(e) =>
                                                                                setSubnetDraft((d) => ({...d, network: e.target.value}))
                                                                            }
                                                                        >
                                                                            <option value="">Select network…</option>
                                                                            {networkNameOptions.map((name) => (
                                                                                <option key={name} value={name}>
                                                                                    {name}
                                                                                </option>
                                                                            ))}
                                                                        </Select>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                                <div className="space-y-1">
                                                                    <Label>CIDR</Label>
                                                                    <input
                                                                        value={subnetDraft.cidr}
                                                                        onChange={(e) => setSubnetDraft((d) => ({...d, cidr: e.target.value}))}
                                                                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                                                                        placeholder="10.0.0.0/24"
                                                                    />
                                                                </div>
                                                                <div className="space-y-1">
                                                                    <Label>Gateway IP (optional)</Label>
                                                                    <input
                                                                        value={subnetDraft.gateway_ip}
                                                                        onChange={(e) => setSubnetDraft((d) => ({...d, gateway_ip: e.target.value}))}
                                                                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                                                                        placeholder="10.0.0.1"
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                                <div className="space-y-1">
                                                                    <Label>IP version</Label>
                                                                    <select
                                                                        value={subnetDraft.ip_version}
                                                                        onChange={(e) =>
                                                                            setSubnetDraft((d) => ({...d, ip_version: Number(e.target.value)}))
                                                                        }
                                                                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                                                                    >
                                                                        <option value={4}>IPv4</option>
                                                                        <option value={6}>IPv6</option>
                                                                    </select>
                                                                </div>
                                                                <div className="flex items-center justify-between gap-2 md:pt-6">
                                                                    <span className="text-sm text-muted-foreground">DHCP</span>
                                                                    <Switch
                                                                        checked={subnetDraft.enable_dhcp}
                                                                        onCheckedChange={(v: boolean) =>
                                                                            setSubnetDraft((d) => ({...d, enable_dhcp: v}))
                                                                        }
                                                                    />
                                                                </div>
                                                                <div className="space-y-1">
                                                                    <Label>DNS preset</Label>
                                                                    <select
                                                                        value={subnetDraft.dns_preset}
                                                                        onChange={(e) =>
                                                                            setSubnetDraft((d) => ({
                                                                                ...d,
                                                                                dns_preset: e.target.value as 'google' | 'none',
                                                                            }))
                                                                        }
                                                                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                                                                    >
                                                                        <option value="google">Google DNS (8.8.8.8, 8.8.4.4)</option>
                                                                        <option value="none">None</option>
                                                                    </select>
                                                                </div>
                                                            </div>

                                                            <div className="space-y-1">
                                                                <Label>Allocation pools (optional)</Label>
                                                                <p className="text-xs text-muted-foreground">
                                                                    One range per line: <code className="rounded bg-muted px-1">start_ip,end_ip</code>{' '}
                                                                    (Neutron allocation pools).
                                                                </p>
                                                                <textarea
                                                                    value={subnetDraft.allocation_pools_text}
                                                                    onChange={(e) =>
                                                                        setSubnetDraft((d) => ({...d, allocation_pools_text: e.target.value}))
                                                                    }
                                                                    rows={4}
                                                                    className="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm outline-none focus:ring-2 focus:ring-primary"
                                                                    placeholder={'10.0.0.10,10.0.0.100\n10.0.0.150,10.0.0.200'}
                                                                />
                                                            </div>
                                                        </div>

                                                        <DialogFooter>
                                                            <Button variant="outline" onClick={() => setAddSubnetDialogOpen(false)}>
                                                                Cancel
                                                            </Button>
                                                            <Button
                                                                disabled={infraActionDisabled}
                                                                onClick={async () => {
                                                                    if (!scenarioId) return;
                                                                    if (!subnetDraft.name.trim()) return toast.error('Subnet name is required.');
                                                                    if (!subnetDraft.network.trim())
                                                                        return toast.error('Select a network for this subnet.');
                                                                    if (!subnetDraft.cidr.trim()) return toast.error('Subnet CIDR is required.');

                                                                    const allocation_pools = parseAllocationPoolsFromForm(
                                                                        subnetDraft.allocation_pools_text
                                                                    );
                                                                    const subnetSpec = {
                                                                        name: subnetDraft.name,
                                                                        network: subnetDraft.network,
                                                                        cidr: subnetDraft.cidr,
                                                                        gateway_ip: subnetDraft.gateway_ip || '',
                                                                        ip_version: Number(subnetDraft.ip_version),
                                                                        enable_dhcp: Boolean(subnetDraft.enable_dhcp),
                                                                        dns_nameservers:
                                                                            subnetDraft.dns_preset === 'google'
                                                                                ? ['8.8.8.8', '8.8.4.4']
                                                                                : [],
                                                                        allocation_pools,
                                                                        ...(editingSubnetId ? {id: editingSubnetId} : {}),
                                                                    };

                                                                    try {
                                                                        await saveInfrastructureConfiguration({
                                                                            exercise_id: scenarioId,
                                                                            subnets: [subnetSpec],
                                                                        }).unwrap();
                                                                        toast.success(editingSubnetId ? 'Subnet updated.' : 'Subnet saved.');
                                                                        await refetch();
                                                                    } catch (err: any) {
                                                                        const detail =
                                                                            err?.data?.detail ??
                                                                            err?.error ??
                                                                            'Failed to save subnet configuration.';
                                                                        toast.error(detail);
                                                                        console.error('Save subnet failed:', err);
                                                                        return;
                                                                    }

                                                                    setSubnetDraft({
                                                                        name: '',
                                                                        network: '',
                                                                        cidr: '',
                                                                        gateway_ip: '',
                                                                        ip_version: 4,
                                                                        enable_dhcp: true,
                                                                        dns_preset: 'google',
                                                                        allocation_pools_text: '',
                                                                    });
                                                                    setEditingSubnetId(null);
                                                                    setAddSubnetDialogOpen(false);
                                                                }}
                                                            >
                                                                {editingSubnetId ? 'Save changes' : 'Add'}
                                                            </Button>
                                                        </DialogFooter>
                                                    </DialogContent>
                                                </Dialog>
                                            </div>
                                        </TabsContent>

                                        <TabsContent value="routers">
                                            <div className="space-y-5">
                                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                                    <div>
                                                        <h3 className="text-base font-semibold text-foreground">Routers</h3>
                                                        <p className="text-sm text-muted-foreground">
                                                            Connect internal networks to a router; optionally mark it as the external gateway.
                                                        </p>
                                                    </div>
                                                    <Button
                                                        className="shrink-0 gap-2"
                                                        disabled={infraActionDisabled}
                                                        onClick={() => {
                                                            setEditingRouterId(null);
                                                            setRouterDraft({
                                                                name: '',
                                                                selectedNetworkNames: [],
                                                                is_external_gateway: true,
                                                                interfacesText: '',
                                                                enable_snat: true,
                                                                admin_state_up: true,
                                                                description: '',
                                                            });
                                                            setAddRouterDialogOpen(true);
                                                        }}
                                                    >
                                                        <Plus className="h-4 w-4"/>
                                                        Add new
                                                    </Button>
                                                </div>

                                                {infra && infra.routers.length > 0 ? (
                                                    <div className="overflow-x-auto rounded-lg border border-border/60 bg-background/50 shadow-sm">
                                                        <table className="w-full text-xs">
                                                            <thead className="bg-muted/60">
                                                            <tr className="text-left">
                                                                <th className="p-2.5 font-medium">Name</th>
                                                                <th className="p-2.5 font-medium">Network / gateway</th>
                                                                <th className="p-2.5 font-medium">SNAT</th>
                                                                <th className="p-2.5 font-medium">State</th>
                                                                <th className="p-2.5 font-medium w-[100px] text-right">Actions</th>
                                                            </tr>
                                                            </thead>
                                                            <tbody>
                                                            {infra.routers.map((r) => (
                                                                <tr key={r.id} className="border-t border-border/60 hover:bg-muted/30">
                                                                    <td className="p-2.5 font-medium text-foreground">{r.name}</td>
                                                                    <td className="p-2.5 max-w-[220px]" title="">
                                                                        {r.internal_networks && r.internal_networks.length > 0 ? (
                                                                            <div className="space-y-1">
                                                                                {r.is_external_gateway ? (
                                                                                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                                                        <Globe className="h-3 w-3 shrink-0"/>
                                                                                        <span>External gateway</span>
                                                                                    </div>
                                                                                ) : null}
                                                                                <ul className="space-y-0.5">
                                                                                    {r.internal_networks.map((n) => (
                                                                                        <li key={n.id} className="truncate text-xs">
                                                                                            {n.name}
                                                                                        </li>
                                                                                    ))}
                                                                                </ul>
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-muted-foreground">—</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="p-2.5">
                                                                        <Badge
                                                                            variant={r.enable_snat ? 'secondary' : 'outline'}
                                                                            className="text-[10px] font-normal"
                                                                        >
                                                                            {r.enable_snat ? 'On' : 'Off'}
                                                                        </Badge>
                                                                    </td>
                                                                    <td className="p-2.5 capitalize">{r.lifecycle_state ?? '—'}</td>
                                                                    <td className="p-2.5">
                                                                        <InfraRowActions
                                                                            disabled={infraActionDisabled}
                                                                            onEdit={() => {
                                                                                setEditingRouterId(r.id);
                                                                                setRouterDraft({
                                                                                    name: r.name,
                                                                                    selectedNetworkNames: (r.internal_networks ?? []).map(
                                                                                        (x) => x.name,
                                                                                    ),
                                                                                    is_external_gateway: r.is_external_gateway !== false,
                                                                                    interfacesText: '',
                                                                                    enable_snat: r.enable_snat ?? true,
                                                                                    admin_state_up: r.admin_state_up ?? true,
                                                                                    description: r.description || '',
                                                                                });
                                                                                setAddRouterDialogOpen(true);
                                                                            }}
                                                                            onDelete={() => {
                                                                                setDeleteTarget({kind: 'router', id: r.id, name: r.name});
                                                                                setDeleteOpen(true);
                                                                            }}
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="rounded-lg border border-dashed border-border/60 bg-muted/20 px-4 py-10 text-center text-sm text-muted-foreground">
                                                        No routers yet. Click <span className="font-medium text-foreground">Add new</span> to
                                                        create one.
                                                    </div>
                                                )}

                                                <Dialog
                                                    open={addRouterDialogOpen}
                                                    onOpenChange={(open) => {
                                                        setAddRouterDialogOpen(open);
                                                        if (!open) setEditingRouterId(null);
                                                    }}
                                                >
                                                    <DialogContent className="sm:max-w-lg">
                                                        <DialogHeader>
                                                            <DialogTitle className="flex items-center gap-2">
                                                                <Router className="h-5 w-5 text-muted-foreground"/>
                                                                {editingRouterId ? 'Edit router' : 'Router'}
                                                            </DialogTitle>
                                                            <DialogDescription>
                                                                Name the router, pick internal networks, and optionally an external gateway.
                                                            </DialogDescription>
                                                        </DialogHeader>

                                                        <div className="space-y-4 py-1">
                                                            <div className="space-y-1.5">
                                                                <Label htmlFor="router-name">Name</Label>
                                                                <input
                                                                    id="router-name"
                                                                    value={routerDraft.name}
                                                                    onChange={(e) => setRouterDraft((d) => ({...d, name: e.target.value}))}
                                                                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                                                                    placeholder="my-router"
                                                                    autoComplete="off"
                                                                />
                                                            </div>

                                                            <div className="space-y-1.5">
                                                                <label className="flex cursor-pointer items-center gap-2 text-sm">
                                                                    <Switch
                                                                        checked={routerDraft.is_external_gateway !== false}
                                                                        onCheckedChange={(v: boolean) =>
                                                                            setRouterDraft((d) => ({...d, is_external_gateway: v}))
                                                                        }
                                                                    />
                                                                    Use external gateway (Neutron)
                                                                </label>
                                                                <p className="text-xs text-muted-foreground">
                                                                    When enabled, Neutron uses the cloud public network configured on the server
                                                                    (e.g. OPENSTACK_PUBLIC_NETWORK_NAME). Internal networks below are optional scenario links.
                                                                </p>
                                                            </div>

                                                            <div className="space-y-1.5">
                                                                <Label>Internal networks</Label>
                                                                {networksSortedForUi.length === 0 ? (
                                                                    <p className="text-xs text-muted-foreground">
                                                                        Add networks on the Networks tab first.
                                                                    </p>
                                                                ) : (
                                                                    <div className="max-h-40 space-y-2 overflow-y-auto rounded-md border border-border/60 bg-muted/20 p-2">
                                                                        {networksSortedForUi.map((n) => {
                                                                            const checked = routerDraft.selectedNetworkNames.includes(n.name);
                                                                            return (
                                                                                <label
                                                                                    key={n.name}
                                                                                    className="flex cursor-pointer items-center gap-2 text-sm"
                                                                                >
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        className="rounded border-border"
                                                                                        checked={checked}
                                                                                        onChange={(e) => {
                                                                                            const on = e.target.checked;
                                                                                            setRouterDraft((d) => {
                                                                                                const next = on
                                                                                                    ? [...d.selectedNetworkNames, n.name]
                                                                                                    : d.selectedNetworkNames.filter(
                                                                                                        (x) => x !== n.name,
                                                                                                    );
                                                                                                return {
                                                                                                    ...d,
                                                                                                    selectedNetworkNames: next,
                                                                                                };
                                                                                            });
                                                                                        }}
                                                                                    />
                                                                                    <span className="truncate">
                                                                                        {n.name}
                                                                                        {n.is_external ? (
                                                                                            <span className="text-muted-foreground"> (external)</span>
                                                                                        ) : null}
                                                                                    </span>
                                                                                </label>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className="flex flex-wrap gap-6">
                                                                <label className="flex cursor-pointer items-center gap-2 text-sm">
                                                                    <Switch
                                                                        checked={routerDraft.enable_snat}
                                                                        onCheckedChange={(v: boolean) =>
                                                                            setRouterDraft((d) => ({...d, enable_snat: v}))
                                                                        }
                                                                    />
                                                                    SNAT
                                                                </label>
                                                                <label className="flex cursor-pointer items-center gap-2 text-sm">
                                                                    <Switch
                                                                        checked={routerDraft.admin_state_up}
                                                                        onCheckedChange={(v: boolean) =>
                                                                            setRouterDraft((d) => ({...d, admin_state_up: v}))
                                                                        }
                                                                    />
                                                                    Admin up
                                                                </label>
                                                            </div>

                                                            <div className="space-y-1.5">
                                                                <Label htmlFor="router-desc">Notes (optional)</Label>
                                                                <input
                                                                    id="router-desc"
                                                                    value={routerDraft.description}
                                                                    onChange={(e) =>
                                                                        setRouterDraft((d) => ({...d, description: e.target.value}))
                                                                    }
                                                                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                                                                    placeholder=""
                                                                    autoComplete="off"
                                                                />
                                                            </div>
                                                        </div>

                                                        <DialogFooter>
                                                            <Button variant="outline" onClick={() => setAddRouterDialogOpen(false)}>
                                                                Cancel
                                                            </Button>
                                                            <Button
                                                                disabled={infraActionDisabled}
                                                                onClick={async () => {
                                                                    if (!scenarioId) return;
                                                                    if (!routerDraft.name.trim()) return toast.error('Router name is required.');

                                                                    const networksNameSet = new Set(networkNameOptions);
                                                                    for (const nn of routerDraft.selectedNetworkNames) {
                                                                        if (!networksNameSet.has(nn)) {
                                                                            return toast.error(
                                                                                `Internal network '${nn}' must match an existing network name.`
                                                                            );
                                                                        }
                                                                    }
                                                                    const internal_networks = routerDraft.selectedNetworkNames.map((name) => ({
                                                                        name,
                                                                    }));

                                                                    const routerSpec = {
                                                                        name: routerDraft.name,
                                                                        description: routerDraft.description,
                                                                        enable_snat: routerDraft.enable_snat,
                                                                        admin_state_up: routerDraft.admin_state_up,
                                                                        is_external_gateway: routerDraft.is_external_gateway !== false,
                                                                        internal_networks,
                                                                        ...(editingRouterId ? {id: editingRouterId} : {}),
                                                                    } as Record<string, unknown>;

                                                                    try {
                                                                        await saveInfrastructureConfiguration({
                                                                            exercise_id: scenarioId,
                                                                            routers: [routerSpec],
                                                                        }).unwrap();
                                                                        toast.success(editingRouterId ? 'Router updated.' : 'Router saved.');
                                                                        await refetch();
                                                                    } catch (err: any) {
                                                                        const detail =
                                                                            err?.data?.detail ??
                                                                            err?.error ??
                                                                            'Failed to save router configuration.';
                                                                        toast.error(detail);
                                                                        console.error('Save router failed:', err);
                                                                        return;
                                                                    }

                                                                    setRouterDraft({
                                                                        name: '',
                                                                        selectedNetworkNames: [],
                                                                        is_external_gateway: true,
                                                                        interfacesText: '',
                                                                        enable_snat: true,
                                                                        admin_state_up: true,
                                                                        description: '',
                                                                    });
                                                                    setEditingRouterId(null);
                                                                    setAddRouterDialogOpen(false);
                                                                }}
                                                            >
                                                                {editingRouterId ? 'Save changes' : 'Add'}
                                                            </Button>
                                                        </DialogFooter>
                                                    </DialogContent>
                                                </Dialog>
                                            </div>
                                        </TabsContent>

                                        <TabsContent value="instances">
                                            <div className="space-y-5">
                                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                                    <div>
                                                        <h3 className="text-base font-semibold text-foreground">Instances</h3>
                                                        <p className="text-sm text-muted-foreground">
                                                            Compute instances (image, flavor, networks).
                                                        </p>
                                                    </div>
                                                    <Button
                                                        className="shrink-0 gap-2"
                                                        disabled={infraActionDisabled}
                                                        onClick={() => {
                                                            setEditingInstanceId(null);
                                                            setInstanceDraft({
                                                                name: '',
                                                                image: '',
                                                                flavor: '',
                                                            });
                                                            setInstanceNetworkAttachments([]);
                                                            setInstanceExerciseTeamMappingId('');
                                                            setAddInstanceDialogOpen(true);
                                                        }}
                                                    >
                                                        <Plus className="h-4 w-4"/>
                                                        Add new
                                                    </Button>
                                                </div>

                                                {infra && infra.instances.length > 0 ? (
                                                    <div className="overflow-x-auto rounded-lg border border-border/60 bg-background/50 shadow-sm">
                                                        <table className="w-full text-xs">
                                                            <thead className="bg-muted/60">
                                                            <tr className="text-left">
                                                                <th className="p-2.5 font-medium">Name</th>
                                                                <th className="p-2.5 font-medium">Image</th>
                                                                <th className="p-2.5 font-medium">Flavor</th>
                                                                <th className="p-2.5 font-medium">Team</th>
                                                                <th className="p-2.5 font-medium">State</th>
                                                                <th className="p-2.5 font-medium w-[100px] text-right">Actions</th>
                                                            </tr>
                                                            </thead>
                                                            <tbody>
                                                            {infra.instances.map((i) => (
                                                                <tr key={i.id} className="border-t border-border/60 hover:bg-muted/30">
                                                                    <td className="p-2.5 font-medium text-foreground">{i.name}</td>
                                                                    <td className="p-2.5 max-w-[140px] truncate">{i.image || '—'}</td>
                                                                    <td className="p-2.5">{i.flavor || '—'}</td>
                                                                    <td className="p-2.5 max-w-[120px] truncate" title={i.team_name || ''}>
                                                                        {i.team_name || '—'}
                                                                    </td>
                                                                    <td className="p-2.5 capitalize">{i.lifecycle_state ?? '—'}</td>
                                                                    <td className="p-2.5">
                                                                        <InfraRowActions
                                                                            disabled={infraActionDisabled}
                                                                            onEdit={() => {
                                                                                setEditingInstanceId(i.id);
                                                                                setInstanceDraft({
                                                                                    name: i.name,
                                                                                    image: i.image || '',
                                                                                    flavor: i.flavor || '',
                                                                                });
                                                                                setInstanceNetworkAttachments(
                                                                                    (i.networks ?? []).map((n) => ({
                                                                                        network: n.network_name,
                                                                                        fixed_ip: (n.fixed_ip || '').trim(),
                                                                                    }))
                                                                                );
                                                                                setInstanceExerciseTeamMappingId(
                                                                                    typeof i.exercise_team_id === 'number' ? i.exercise_team_id : ''
                                                                                );
                                                                                setAddInstanceDialogOpen(true);
                                                                            }}
                                                                            onDelete={() => {
                                                                                setDeleteTarget({kind: 'instance', id: i.id, name: i.name});
                                                                                setDeleteOpen(true);
                                                                            }}
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="rounded-lg border border-dashed border-border/60 bg-muted/20 px-4 py-10 text-center text-sm text-muted-foreground">
                                                        No instances yet. Click <span className="font-medium text-foreground">Add new</span> to
                                                        create one.
                                                    </div>
                                                )}

                                                <Dialog
                                                    open={addInstanceDialogOpen}
                                                    onOpenChange={(open) => {
                                                        setAddInstanceDialogOpen(open);
                                                        if (!open) setEditingInstanceId(null);
                                                    }}
                                                >
                                                    <DialogContent>
                                                        <DialogHeader>
                                                            <DialogTitle className="flex items-center gap-2">
                                                                <Server className="h-5 w-5 text-muted-foreground"/>
                                                                {editingInstanceId ? 'Edit instance' : 'Add instance'}
                                                            </DialogTitle>
                                                            <DialogDescription>Save instance configuration for this exercise.</DialogDescription>
                                                        </DialogHeader>

                                                        <div className="space-y-3">
                                                            {(flavorsError || imagesError) && (
                                                                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                                                                    Could not load Image/Flavor lists from OpenStack.{' '}
                                                                    <button
                                                                        type="button"
                                                                        className="underline font-medium"
                                                                        onClick={() => {
                                                                            void refetchFlavors();
                                                                            void refetchImages();
                                                                        }}
                                                                    >
                                                                        Retry
                                                                    </button>
                                                                </div>
                                                            )}

                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                                <div className="space-y-1">
                                                                    <Label>Instance name</Label>
                                                                    <input
                                                                        value={instanceDraft.name}
                                                                        onChange={(e) =>
                                                                            setInstanceDraft((d) => ({...d, name: e.target.value}))
                                                                        }
                                                                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                                                                        placeholder="vm1"
                                                                    />
                                                                </div>
                                                                <div className="space-y-1">
                                                                    <Label>Image</Label>
                                                                    <select
                                                                        value={instanceDraft.image}
                                                                        onChange={(e) =>
                                                                            setInstanceDraft((d) => ({...d, image: e.target.value}))
                                                                        }
                                                                        disabled={imagesLoading}
                                                                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
                                                                    >
                                                                        <option value="">
                                                                            {imagesLoading ? 'Loading images…' : 'Select an image…'}
                                                                        </option>
                                                                        {imageSelectOptions.map((row) => (
                                                                            <option key={row.id} value={row.name}>
                                                                                {row.name}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    <p className="text-xs text-muted-foreground">
                                                                        Options from your OpenStack cloud (exercise cloud:{' '}
                                                                        {infra?.cloud_name || 'default'}).
                                                                    </p>
                                                                </div>
                                                            </div>

                                                            <div className="space-y-1">
                                                                <Label>Flavor</Label>
                                                                <select
                                                                    value={instanceDraft.flavor}
                                                                    onChange={(e) =>
                                                                        setInstanceDraft((d) => ({...d, flavor: e.target.value}))
                                                                    }
                                                                    disabled={flavorsLoading}
                                                                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
                                                                >
                                                                    <option value="">
                                                                        {flavorsLoading ? 'Loading flavors…' : 'Select a flavor…'}
                                                                    </option>
                                                                    {flavorSelectOptions.map((row) => {
                                                                        const f = row as {
                                                                            id: string;
                                                                            name: string;
                                                                            vcpus?: number | null;
                                                                            ram_mb?: number | null;
                                                                        };
                                                                        const extra =
                                                                            f.vcpus != null && f.ram_mb != null
                                                                                ? ` (${f.vcpus} vCPU, ${f.ram_mb} MB)`
                                                                                : '';
                                                                        return (
                                                                            <option key={f.id} value={f.name}>
                                                                                {f.name}
                                                                                {extra}
                                                                            </option>
                                                                        );
                                                                    })}
                                                                </select>
                                                            </div>

                                                            <div className="space-y-1">
                                                                <Label>
                                                                    Team <span className="text-destructive">*</span>
                                                                </Label>
                                                                {exerciseTeamMappingOptions.length === 0 ? (
                                                                    <p className="text-sm text-muted-foreground rounded-md border border-dashed border-border/60 bg-muted/20 px-3 py-2">
                                                                        No team mappings for this exercise yet. Add teams under{' '}
                                                                        <span className="font-medium text-foreground">Exercise team mappings</span> on
                                                                        the scenario before creating an instance.
                                                                    </p>
                                                                ) : (
                                                                    <select
                                                                        required
                                                                        value={instanceExerciseTeamMappingId === '' ? '' : String(instanceExerciseTeamMappingId)}
                                                                        onChange={(e) => {
                                                                            const v = e.target.value;
                                                                            setInstanceExerciseTeamMappingId(v === '' ? '' : Number(v));
                                                                        }}
                                                                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                                                                    >
                                                                        <option value="" disabled>
                                                                            Select a team…
                                                                        </option>
                                                                        {exerciseTeamMappingOptions.map((m) => (
                                                                            <option key={m.id} value={m.id}>
                                                                                {m.team_name}
                                                                                {m.team_code ? ` (${m.team_code})` : ''}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                )}
                                                                <p className="text-xs text-muted-foreground">
                                                                    Required. Associates this instance with a team for access and reporting.
                                                                </p>
                                                            </div>

                                                            <div className="space-y-1">
                                                                <Label>Networks &amp; static IP</Label>
                                                                {networkNameOptions.length === 0 ? (
                                                                    <p className="text-sm text-muted-foreground rounded-md border border-dashed border-border/60 bg-muted/20 px-3 py-2">
                                                                        Add and save at least one network on the <span className="font-medium text-foreground">Networks</span> tab
                                                                        first. Then
                                                                        your networks will appear here.
                                                                    </p>
                                                                ) : (
                                                                    <div className="rounded-md border border-border bg-background px-3 py-2 space-y-0 max-h-56 overflow-y-auto">
                                                                        {networkNameOptions.map((name) => {
                                                                            const att = instanceNetworkAttachments.find((x) => x.network === name);
                                                                            const checked = Boolean(att);
                                                                            return (
                                                                                <div
                                                                                    key={name}
                                                                                    className="flex flex-col gap-1.5 py-2 border-b border-border/40 last:border-0 sm:flex-row sm:items-center sm:gap-3"
                                                                                >
                                                                                    <label
                                                                                        className="flex items-center gap-2 text-sm cursor-pointer select-none shrink-0 min-w-[140px]">
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            className="rounded border-border"
                                                                                            checked={checked}
                                                                                            onChange={() => toggleInstanceNetwork(name)}
                                                                                        />
                                                                                        <span>{name}</span>
                                                                                    </label>
                                                                                    {checked && (
                                                                                        <input
                                                                                            type="text"
                                                                                            autoComplete="off"
                                                                                            value={att?.fixed_ip ?? ''}
                                                                                            onChange={(e) => setInstanceNetworkFixedIp(name, e.target.value)}
                                                                                            className="flex-1 min-w-0 rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-primary"
                                                                                            placeholder="Static IP (optional, e.g. 10.0.0.50)"
                                                                                        />
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                                <p className="text-xs text-muted-foreground">
                                                                    Select networks to attach. Optionally set a fixed IP per network (must fit the
                                                                    subnet CIDR). Leave blank to let DHCP assign an address.
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <DialogFooter>
                                                            <Button variant="outline" onClick={() => setAddInstanceDialogOpen(false)}>
                                                                Cancel
                                                            </Button>
                                                            <Button
                                                                disabled={infraActionDisabled}
                                                                onClick={async () => {
                                                                    if (!scenarioId) return;
                                                                    if (!instanceDraft.name.trim())
                                                                        return toast.error('Instance name is required.');
                                                                    if (!instanceDraft.image.trim()) return toast.error('Image is required.');
                                                                    if (!instanceDraft.flavor.trim()) return toast.error('Flavor is required.');
                                                                    if (exerciseTeamMappingOptions.length === 0) {
                                                                        return toast.error(
                                                                            'Add at least one exercise team mapping on this scenario before creating an instance.'
                                                                        );
                                                                    }
                                                                    if (instanceExerciseTeamMappingId === '') {
                                                                        return toast.error('Team is required.');
                                                                    }
                                                                    if (instanceNetworkAttachments.length === 0)
                                                                        return toast.error('Select at least one network.');

                                                                    const networkNameSet = new Set(networkNameOptions);
                                                                    for (const {network: netName} of instanceNetworkAttachments) {
                                                                        if (!networkNameSet.has(netName)) {
                                                                            return toast.error(
                                                                                `Instance network '${netName}' must match an existing network name.`
                                                                            );
                                                                        }
                                                                    }

                                                                    const networks = instanceNetworkAttachments.map(({network, fixed_ip}) => {
                                                                        const ip = fixed_ip.trim();
                                                                        return ip ? {name: network, fixed_ip: ip} : network;
                                                                    });

                                                                    const instanceSpec = {
                                                                        ...instanceDraft,
                                                                        networks,
                                                                        availability_zone: '',
                                                                        min_count: 1,
                                                                        max_count: 1,
                                                                        desired_count: 1,
                                                                        exercise_team_id: Number(instanceExerciseTeamMappingId),
                                                                        ...(editingInstanceId ? {id: editingInstanceId} : {}),
                                                                    } as any;

                                                                    try {
                                                                        await saveInfrastructureConfiguration({
                                                                            exercise_id: scenarioId,
                                                                            instances: [instanceSpec],
                                                                        }).unwrap();
                                                                        toast.success(editingInstanceId ? 'Instance updated.' : 'Instance saved.');
                                                                        await refetch();
                                                                    } catch (err: any) {
                                                                        const detail =
                                                                            err?.data?.detail ??
                                                                            err?.error ??
                                                                            'Failed to save instance configuration.';
                                                                        toast.error(detail);
                                                                        console.error('Save instance failed:', err);
                                                                        return;
                                                                    }

                                                                    setInstanceDraft({
                                                                        name: '',
                                                                        image: '',
                                                                        flavor: '',
                                                                    });
                                                                    setInstanceNetworkAttachments([]);
                                                                    setInstanceExerciseTeamMappingId('');
                                                                    setEditingInstanceId(null);
                                                                    setAddInstanceDialogOpen(false);
                                                                }}
                                                            >
                                                                {editingInstanceId ? 'Save changes' : 'Add'}
                                                            </Button>
                                                        </DialogFooter>
                                                    </DialogContent>
                                                </Dialog>
                                            </div>
                                        </TabsContent>
                                    </div>
                                </Tabs>

                                <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Delete infrastructure resource?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                Remove{' '}
                                                <span className="font-medium text-foreground">
                              {deleteTarget?.name ?? 'this resource'}
                            </span>{' '}
                                                ({deleteTarget?.kind ?? 'resource'}) from this exercise? This cannot be undone.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel type="button">Cancel</AlertDialogCancel>
                                            <Button
                                                type="button"
                                                variant="destructive"
                                                disabled={infraActionDisabled}
                                                onClick={() => void handleConfirmDelete()}
                                            >
                                                {infraDeleting ? 'Deleting…' : 'Delete'}
                                            </Button>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            <InjectsSidebar
                isOpen={showInjectsSidebar}
                onClose={closeInjectsSidebar}
                title={
                    sidebarPanel === 'injects'
                        ? 'Scenario Injects'
                        : sidebarPanel === 'exercise-phases'
                            ? 'Exercise Phases'
                            : sidebarPanel === 'team-mappings'
                                ? 'Exercise Team Mappings'
                                : ''
                }
            >
                {sidebarPanel === 'injects' && <ScenarioInjectsTab scenarioId={scenario.id}/>}
                {sidebarPanel === 'exercise-phases' && (
                    <ExercisePhasesCard
                        scenario={scenario}
                        onRefresh={fetchScenario}
                        locked={infraLockedByActiveRun}
                    />
                )}
                {sidebarPanel === 'team-mappings' && (
                    <ExerciseTeamMappingsCard
                        exerciseId={scenario.id}
                        initialMappings={scenario.exercise_team_mappings}
                        onRefetch={fetchScenario}
                        locked={infraLockedByActiveRun}
                    />
                )}
            </InjectsSidebar>

            <AlertDialog open={publishConfirmOpen} onOpenChange={setPublishConfirmOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Publish this scenario?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Publishing makes this scenario available for public use in the dashboard.
                            Users with access will be able to start and use it.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel type="button">Cancel</AlertDialogCancel>
                        <Button
                            type="button"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            disabled={approvalUpdating}
                            onClick={async () => {
                                setPublishConfirmOpen(false);
                                await handleApprove();
                            }}
                        >
                            {approvalUpdating ? 'Publishing…' : 'Publish'}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={markReadyConfirmOpen} onOpenChange={setMarkReadyConfirmOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Mark this scenario as ready for publish?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This confirms the scenario setup is complete and it can move to the publish step.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel type="button">Cancel</AlertDialogCancel>
                        <Button
                            type="button"
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            disabled={preparedUpdating}
                            onClick={async () => {
                                setMarkReadyConfirmOpen(false);
                                await handlePrepare();
                            }}
                        >
                            {preparedUpdating ? 'Marking ready for publish…' : 'Mark Ready'}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

        </div>
    );
}
